/* eslint-disable react/jsx-props-no-spreading */
import React, {
  forwardRef, useContext, useEffect, useMemo,
} from 'react';
import { TransitionProps } from '@mui/material/transitions';
import {
  Button,
  Dialog, DialogContent, DialogTitle, Grid, IconButton, Zoom,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';
import { TypesDialogBox } from '../../../../utils/interface';
import { LocalizationContext } from '../../../../Common/Localization/LocalizationProvider';
import { AddFlaggedKitsData, FlaggedKits } from '../utils/types';
import ComponentTextInput from '../../../../Common/ComponentTextinput/ComponentTextInput';
import { FlaggedKitsFormValue } from '../utils/ConstantsFlaggedKits';
import { RootState } from '../../../../redux/rootState';
import { flaggedKitsSchema } from '../utils/validations';
import {
  addFlaggedKitsStart, resetState, updateFlaggedKits,
} from '../redux/sliceFlaggedKits';

const Transition = forwardRef((
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) => <Zoom ref={ref} {...props} />);

function ComponentFlaggedKitsDialog({
  show, setOpenFrom, rowId, handleData,
}: TypesDialogBox): JSX.Element {
  const { translations } = useContext(LocalizationContext);
  const { isSuccess, FlaggedKitsData } = useSelector((state: RootState) => state.FlaggedKitsReducer);

  const dispatch = useDispatch();

  const {
    formState: { errors }, control, handleSubmit, setValue, reset,
  } = useForm<AddFlaggedKitsData>({
    defaultValues: {
      kitNo: '',
    },
    resolver: yupResolver(flaggedKitsSchema as ObjectSchema<AddFlaggedKitsData>),
  });

  const resetValues = () => {
    setValue(FlaggedKitsFormValue.KIT_NO, '');
  };

  const onSubmit = (data: AddFlaggedKitsData) => {
    if (rowId) {
      const updateData = {
        Id: rowId,
        kitNo: data?.kitNo,
      };
      dispatch(updateFlaggedKits(updateData));
      reset();
      setOpenFrom();
    } else {
      const bodyData = {
        kitNo: data?.kitNo,
      };
      dispatch(addFlaggedKitsStart(bodyData));
      reset();
      setOpenFrom();
    }
  };

  const tableData: FlaggedKits[] = useMemo(() => FlaggedKitsData?.Data?.map((item, index) => {
    const transformedDataItem = {
      sn: index,
      action: 'edit&delete',
      text: item?.Text,
      id: String(item?.Value),
    };
    return transformedDataItem;
  }), [FlaggedKitsData]);

  useEffect(() => {
    if (rowId && tableData) {
      tableData?.forEach((rowData) => {
        if (rowData?.id === rowId) {
          setValue(FlaggedKitsFormValue.KIT_NO, rowData.text);
        }
      });
    }
  }, [rowId, tableData]);

  useEffect(() => {
    if (isSuccess) {
      resetValues();
      if (handleData) {
        handleData();
      }
      dispatch(resetState());
    }
  }, [isSuccess]);

  const handleClose = () => {
    setOpenFrom();
    reset();
    setValue(FlaggedKitsFormValue.KIT_NO, '');
  };

  const getAnalystHeading = () => {
    if (rowId) {
      return 'Edit Kit #';
    }
    return 'Add Kit #';
  };

  return (
    <div>
      <Dialog
        maxWidth="sm"
        PaperProps={{
          className: 'paperStyle',
        }}
        open={show}
        transitionDuration={500}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle className="modalHeaderTitle">
          {getAnalystHeading()}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ padding: '16px 24px' }}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Grid container spacing={2}>
              <Grid item md={12} sm={12} xs={12}>
                <div className="div_label_text">
                  <label className="label_Style_job">Kit#</label>
                  <ComponentTextInput
                    control={control}
                    className="nonCapitalizeField"
                    inputProps={{ maxLength: 50 }}
                    name={FlaggedKitsFormValue.KIT_NO}
                    size="small"
                    id={FlaggedKitsFormValue.KIT_NO}
                    errors={errors}
                    type="number"
                    onKeyDown={(event) => {
                      if (['-', '+', 'e', 'E'].includes(event.key)) {
                        event.preventDefault();
                      }
                    }}
                  />
                </div>
              </Grid>
              <Grid item md={12} sm={12} xs={12}>
                <div className="save_and_next_div">
                  <div>
                    <Button className="button_cancel" onClick={handleClose}>{translations.cancel}</Button>
                  </div>
                  <div className="button_margin_left">
                    <Button type="submit" className="button_save_and_next">{translations.save}</Button>
                  </div>
                </div>
              </Grid>
            </Grid>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ComponentFlaggedKitsDialog;
